/**
 * ConversationChat — Chat tipo ChatGPT para crear segmentos por conversación.
 *
 * Diseño:
 *  - Empty state grande con quick prompts clickeables
 *  - Bubbles con avatares (User azul · IA gradient violeta)
 *  - Typing indicator de 3 dots animados mientras la IA "piensa"
 *  - Footer con count en cada respuesta del bot
 *  - Suggestions chips ricos debajo del último mensaje
 *  - Input grande tipo textarea con auto-resize, Send con shortcut Enter
 */

import { useState, useRef, useEffect } from "react";
import {
    Send,
    Undo2,
    Loader2,
    Sparkles,
    User,
    Users,
    ArrowRight,
} from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { ConversationTurn, Suggestion } from "../../types";

interface ConversationChatProps {
    history: ConversationTurn[];
    suggestions: Suggestion[];
    loading: boolean;
    disabled?: boolean;
    canUndo: boolean;
    onSendMessage: (message: string) => void;
    onSelectSuggestion: (suggestion: Suggestion) => void;
    onUndo: () => void;
}

/** Prompts grandes para el empty state — el "quick start" del chat */
const QUICK_PROMPTS = [
    {
        emoji: "💎",
        title: "Clientes VIP",
        prompt: "clientes VIP que gastaron más de 30 mil",
        color: "from-violet-100 to-violet-50 text-violet-700",
    },
    {
        emoji: "😴",
        title: "Inactivos 30 días",
        prompt: "clientes que no pidieron hace 30 días o más",
        color: "from-slate-100 to-slate-50 text-slate-700",
    },
    {
        emoji: "🛒",
        title: "Carritos abandonados",
        prompt: "usuarios con carrito abandonado en las últimas 24 horas",
        color: "from-amber-100 to-amber-50 text-amber-700",
    },
    {
        emoji: "👋",
        title: "Primera compra",
        prompt: "clientes que hicieron su primera compra pero todavía no la segunda",
        color: "from-emerald-100 to-emerald-50 text-emerald-700",
    },
    {
        emoji: "📍",
        title: "Solo Belgrano",
        prompt: "clientes habituales del local Belgrano",
        color: "from-blue-100 to-blue-50 text-blue-700",
    },
    {
        emoji: "🎂",
        title: "Cumpleañeros del mes",
        prompt: "clientes que cumplen años este mes",
        color: "from-pink-100 to-pink-50 text-pink-700",
    },
];

export function ConversationChat({
    history,
    suggestions,
    loading,
    disabled,
    canUndo,
    onSendMessage,
    onSelectSuggestion,
    onUndo,
}: ConversationChatProps) {
    const safeHistory = history || [];
    const safeSuggestions = suggestions || [];

    const [input, setInput] = useState("");
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-scroll on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
        });
    }, [safeHistory.length, loading]);

    // Auto-resize textarea
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
    }, [input]);

    const handleSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (input.trim() && !loading && !disabled) {
            onSendMessage(input.trim());
            setInput("");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const isEmpty = safeHistory.length === 0;

    return (
        <div className="flex flex-col h-full bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-gradient-to-r from-violet-50/80 via-white to-fuchsia-50/40">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-sm">
                            <Sparkles className="text-white" size={16} />
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900 text-sm">
                            Asistente de segmentación
                        </h3>
                        <p className="text-[11px] text-slate-500 flex items-center gap-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${loading ? "bg-amber-400 animate-pulse" : "bg-green-500"}`} />
                            {loading ? "Pensando…" : "En línea"}
                        </p>
                    </div>
                </div>
                {canUndo && (
                    <button
                        onClick={onUndo}
                        disabled={loading}
                        className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-50"
                    >
                        <Undo2 size={13} />
                        Deshacer
                    </button>
                )}
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-[420px]">
                {isEmpty ? (
                    <EmptyState
                        onPick={(prompt) => onSendMessage(prompt)}
                        disabled={loading || !!disabled}
                    />
                ) : (
                    safeHistory.map((turn, idx) => (
                        <MessageBubble key={idx} turn={turn} />
                    ))
                )}

                {/* Typing indicator */}
                {loading && (
                    <div className="flex items-start gap-2.5 animate-fade-in">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                            <Sparkles size={14} className="text-white" />
                        </div>
                        <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3">
                            <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" />
                                <span
                                    className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "150ms" }}
                                />
                                <span
                                    className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "300ms" }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {safeSuggestions.length > 0 && !loading && (
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <Sparkles size={10} className="text-violet-500" />
                        Refiná con un click
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {safeSuggestions.map((suggestion, idx) => (
                            <button
                                key={idx}
                                onClick={() => onSelectSuggestion(suggestion)}
                                disabled={loading || disabled}
                                className="group/sug px-3 py-1.5 text-xs font-medium bg-white text-slate-700 rounded-full border border-slate-200 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50 active:scale-95 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {suggestion.label}
                                <ArrowRight
                                    size={11}
                                    className="opacity-0 -ml-1 transition-all group-hover/sug:opacity-100 group-hover/sug:ml-0"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input */}
            <form
                onSubmit={handleSubmit}
                className="p-3 border-t border-slate-100 bg-white"
            >
                <div className="relative flex items-end gap-2 bg-slate-50 border border-slate-200 rounded-2xl px-3 py-2 focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100 transition-all">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Describí tu audiencia... (Shift+Enter = nueva línea)"
                        rows={1}
                        className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none resize-none py-1.5 max-h-[120px]"
                        disabled={loading || disabled}
                    />
                    <button
                        type="submit"
                        disabled={!input.trim() || loading || disabled}
                        className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-sm hover:scale-105 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 flex-shrink-0"
                        aria-label="Enviar"
                    >
                        {loading ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Send size={15} />
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}

/* ─── Sub: Empty state con quick prompts ─── */
function EmptyState({
    onPick,
    disabled,
}: {
    onPick: (prompt: string) => void;
    disabled: boolean;
}) {
    return (
        <div className="flex flex-col items-center justify-center h-full text-center py-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md mb-4">
                <Sparkles className="text-white" size={26} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1.5">
                ¿Qué audiencia querés crear?
            </h3>
            <p className="text-sm text-slate-500 mb-6 max-w-md leading-relaxed">
                Describilo en lenguaje natural — la IA arma el filtro y te muestra
                cuántos coinciden. Probá uno de estos ejemplos o escribí tu pedido:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 w-full max-w-2xl">
                {QUICK_PROMPTS.map((p) => (
                    <button
                        key={p.title}
                        onClick={() => onPick(p.prompt)}
                        disabled={disabled}
                        className={`group/qp text-left p-3 rounded-xl bg-gradient-to-br ${p.color} border border-white/60 hover:scale-[1.02] active:scale-100 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        <div className="flex items-start gap-2">
                            <span className="text-xl flex-shrink-0">{p.emoji}</span>
                            <div className="min-w-0 flex-1">
                                <p className="font-semibold text-xs leading-tight">
                                    {p.title}
                                </p>
                                <p className="text-[10px] opacity-70 mt-0.5 line-clamp-2 leading-snug">
                                    {p.prompt}
                                </p>
                            </div>
                        </div>
                    </button>
                ))}
            </div>

            <p className="text-[11px] text-slate-400 mt-6 italic">
                💡 La IA acepta lenguaje informal y modismos
            </p>
        </div>
    );
}

/* ─── Sub: Bubble de un mensaje ─── */
function MessageBubble({ turn }: { turn: ConversationTurn }) {
    const isUser = turn.role === "user";

    return (
        <div
            className={`flex items-start gap-2.5 animate-fade-in ${
                isUser ? "flex-row-reverse" : ""
            }`}
        >
            {/* Avatar */}
            <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isUser
                        ? "bg-gradient-to-br from-blue-500 to-indigo-500"
                        : "bg-gradient-to-br from-violet-500 to-fuchsia-500"
                }`}
            >
                {isUser ? (
                    <User size={14} className="text-white" />
                ) : (
                    <Sparkles size={14} className="text-white" />
                )}
            </div>

            {/* Bubble */}
            <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    isUser
                        ? "bg-violet-600 text-white rounded-tr-sm"
                        : "bg-slate-100 text-slate-800 rounded-tl-sm"
                }`}
            >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {turn.content}
                </p>
                {turn.role === "assistant" && turn.countSnapshot != null && (
                    <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex items-center gap-1.5 text-xs text-slate-700">
                        <Users size={11} className="text-violet-500" />
                        <span className="font-bold text-violet-700 tabular-nums">
                            {turn.countSnapshot.toLocaleString("es-AR")}
                        </span>
                        <span className="text-slate-500">usuarios coinciden</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ConversationChat;
